import React, { PropsWithChildren, useCallback, memo } from 'react';
import Dialog, { DialogProps } from '@/components/atom/Dialog';
import Button from '@/components/atom/Button';
import { BUTTON_CONST } from '@/src/constants/buttons';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Loader2Icon } from 'lucide-react';

interface ConfirmDialogProps extends PropsWithChildren<DialogProps> {
  onOk?: () => void;
  onCancel?: () => void;
  cancelText?: string;
  okText?: string;
  cancelButtonProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Cancel
  >;
  okButtonProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Action
  >;
  isLoading?: boolean;
}

const MemoizedDialog = memo(Dialog);

function ConfirmDialog({
  children,
  onOpenChange,
  onOk,
  onCancel,
  okText,
  cancelText,
  cancelButtonProps,
  okButtonProps,
  isLoading,
  ...props
}: ConfirmDialogProps) {
  const handleOkBtnClick = useCallback(() => {
    onOpenChange?.(false);
    onOk?.();
  }, [onOk, onOpenChange]);

  const handleCancelBtnClick = useCallback(() => {
    onOpenChange?.(false);
    onCancel?.();
  }, [onCancel, onOpenChange]);

  return (
    <MemoizedDialog
      onOpenChange={onOpenChange}
      overlayClassName="bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            className="flex-1"
            buttonColor="outline"
            onClick={handleCancelBtnClick}
            {...cancelButtonProps}
          >
            {cancelText ?? BUTTON_CONST.cancel}
          </Button>
          <Button
            className="flex-1"
            onClick={handleOkBtnClick}
            {...okButtonProps}
          >
            {isLoading && (
              <Loader2Icon size={16} className="mr-2 animate-spin" />
            )}{' '}
            {okText ?? BUTTON_CONST.ok}
          </Button>
        </div>
      }
      {...props}
    >
      {children}
    </MemoizedDialog>
  );
}

export default React.memo(ConfirmDialog);
